import * as GeoJSON from 'geojson'; // Import if you installed @types/geojson

// Define the structure for options
interface GpxOptions {
  creator?: string;
  metadata?: Record<string, any>; // Simple object for metadata
  featureTitle?: (properties: GeoJSON.GeoJsonProperties) => string | undefined;
  featureDescription?: (properties: GeoJSON.GeoJsonProperties) => string | undefined;
  featureLink?: (properties: GeoJSON.GeoJsonProperties) => string | undefined;
  // Can be a function getting the feature, or a string key for the property
  featureCoordTimes?: ((feature: GeoJSON.Feature) => (string | number | Date)[] | null) | string;
}

// Helper function to escape XML characters
function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&"']/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '"': return '&quot;';
      case "'": return '&apos;';
      default: return c; // Should not happen with the regex
    }
  });
}

// --- Default Option Implementations (same as before) ---
// (defaultFeatureTitle, defaultFeatureDescription, defaultFeatureCoordTimes)
// ... keep these functions as they were ...
function defaultFeatureTitle(props: GeoJSON.GeoJsonProperties | null | undefined): string | undefined {
  if (!props) return undefined;
  if (props.name) return String(props.name);
  if (props.title) return String(props.title);
  if (props.ref) return String(props.ref);
  if (props.id) return String(props.id);
  if (props.tags && typeof props.tags === 'object') {
    const tags = props.tags as Record<string, any>;
    if (tags.name) return String(tags.name);
    if (tags.ref) return String(tags.ref);
  }
  return undefined;
}

function defaultFeatureDescription(props: GeoJSON.GeoJsonProperties | null | undefined): string | undefined {
  if (!props) return undefined;
  let desc = '';
  for (const k in props) {
    if (Object.prototype.hasOwnProperty.call(props, k) && typeof props[k] !== 'object') {
      desc += `${k}=${props[k]}\n`;
    } else if (k === 'tags' && typeof props.tags === 'object' && props.tags !== null) {
      const tags = props.tags as Record<string, any>;
      for (const tk in tags) {
        if (Object.prototype.hasOwnProperty.call(tags, tk)) {
          desc += `${tk}=${tags[tk]}\n`;
        }
      }
    }
  }
  return desc.trim() || undefined;
}

function defaultFeatureCoordTimes(feature: GeoJSON.Feature): (string | number | Date)[] | null {
  if (!feature.properties) return null;
  return feature.properties.times || feature.properties.coordTimes || null;
}


// --- The Main Conversion Function ---

export function geojsonToGpx(
  geojson: GeoJSON.GeoJSON, // Accept Feature, FeatureCollection, or Geometry
  options?: GpxOptions
): string {

  // --- Moved formatTime function here ---
  const formatTime = (timeInput: string | number | Date | undefined | null): string | undefined => {
    if (timeInput === undefined || timeInput === null) return undefined;
    try {
      const date = new Date(timeInput);
      if (isNaN(date.getTime())) return undefined; // Invalid date
      return date.toISOString(); // Returns in YYYY-MM-DDTHH:mm:ss.sssZ format
    } catch (e) {
      console.error("Error formatting time:", timeInput, e); // Log error for debugging
      return undefined; // Error parsing date
    }
  }
  // --- End of moved formatTime ---

  const mergedOptions: Required<Omit<GpxOptions, 'metadata' | 'featureLink' | 'featureCoordTimes'>> & Pick<GpxOptions, 'metadata' | 'featureLink'> & { featureCoordTimesFn: (feature: GeoJSON.Feature) => (string | number | Date)[] | null } = {
    creator: options?.creator ?? "geojson-to-gpx-ts",
    metadata: options?.metadata,
    featureTitle: options?.featureTitle ?? defaultFeatureTitle,
    featureDescription: options?.featureDescription ?? defaultFeatureDescription,
    featureLink: options?.featureLink,
    // Resolve featureCoordTimes option
    featureCoordTimesFn: typeof options?.featureCoordTimes === 'string'
      ? (feature: GeoJSON.Feature) => feature.properties?.[options.featureCoordTimes as string] ?? null
      : options?.featureCoordTimes ?? defaultFeatureCoordTimes,
  };

  let features: GeoJSON.Feature[] = [];

  // --- Normalize input GeoJSON to an array of Features (same as before) ---
  if (geojson.type === "FeatureCollection") {
    features = geojson.features.filter(f => f !== null && f.geometry) as GeoJSON.Feature[];
  } else if (geojson.type === "Feature") {
    if (geojson.geometry) {
      features = [geojson];
    }
  } else {
    features = [{ type: "Feature", properties: {}, geometry: geojson }];
  }

  // --- Build GPX parts ---
  let gpxWaypoints = '';
  let gpxTracks = '';

  // --- processFeature now uses formatTime from the outer scope ---
  const processFeature = (feature: GeoJSON.Feature) => {
    const geometry = feature.geometry;
    const properties = feature.properties ?? {};
    const title = mergedOptions.featureTitle(properties);
    const description = mergedOptions.featureDescription(properties);
    const link = mergedOptions.featureLink ? mergedOptions.featureLink(properties) : undefined;
    const times = mergedOptions.featureCoordTimesFn(feature);

    // No need to define formatTime here anymore

    // --- Handle Geometry Types (Switch statement remains the same) ---
    switch (geometry.type) {
      case "Point": {
        const coords = geometry.coordinates;
        const wpt = `<wpt lat="${escapeXml(String(coords[1]))}" lon="${escapeXml(String(coords[0]))}">` +
          (coords.length > 2 && coords[2] !== null ? `<ele>${escapeXml(String(coords[2]))}</ele>` : '') +
          (title ? `<name>${escapeXml(title)}</name>` : '') +
          (description ? `<desc>${escapeXml(description)}</desc>` : '') +
          (link ? `<link href="${escapeXml(link)}"><text>Link</text></link>` : '') +
          `</wpt>\n`;
        gpxWaypoints += wpt;
        break;
      }
      case "MultiPoint": {
        geometry.coordinates.forEach(coords => { // coords is [lon, lat, ele?]
          const wpt = `<wpt lat="${escapeXml(String(coords[1]))}" lon="${escapeXml(String(coords[0]))}">` +
            (coords.length > 2 && coords[2] !== null ? `<ele>${escapeXml(String(coords[2]))}</ele>` : '') +
            (title ? `<name>${escapeXml(title)}</name>` : '') + // Use same title/desc for all points in MultiPoint
            (description ? `<desc>${escapeXml(description)}</desc>` : '') +
            (link ? `<link href="${escapeXml(link)}"><text>Link</text></link>` : '') +
            `</wpt>\n`;
          gpxWaypoints += wpt;
        });
        break;
      }

      case "LineString": {
        let trackSegments = '';
        let segmentPoints = '';
        geometry.coordinates.forEach((coords, index) => { // coords is [lon, lat, ele?]
          const timeStr = times && index < times.length ? formatTime(times[index]) : undefined; // Now calls outer formatTime
          segmentPoints += `<trkpt lat="${escapeXml(String(coords[1]))}" lon="${escapeXml(String(coords[0]))}">` +
            (coords.length > 2 && coords[2] !== null ? `<ele>${escapeXml(String(coords[2]))}</ele>` : '') +
            (timeStr ? `<time>${escapeXml(timeStr)}</time>` : '') +
            `</trkpt>\n`;
        });
        if (segmentPoints) {
          trackSegments += `<trkseg>\n${segmentPoints}</trkseg>\n`;
        }
        if (trackSegments) {
          gpxTracks += `<trk>\n` +
            (title ? `<name>${escapeXml(title)}</name>\n` : '') +
            (description ? `<desc>${escapeXml(description)}</desc>\n` : '') +
            (link ? `<link href="${escapeXml(link)}"><text>Link</text></link>\n` : '') +
            trackSegments +
            `</trk>\n`;
        }
        break;
      }
      case "MultiLineString": {
        let trackSegments = '';
        let timeIndexOffset = 0; // Keep track for times array across segments
        geometry.coordinates.forEach(lineCoords => { // lineCoords is array of [lon, lat, ele?]
          let segmentPoints = '';
          lineCoords.forEach((coords, index) => {
            const currentTimeIndex = timeIndexOffset + index;
            const timeStr = times && currentTimeIndex < times.length ? formatTime(times[currentTimeIndex]) : undefined; // Now calls outer formatTime
            segmentPoints += `<trkpt lat="${escapeXml(String(coords[1]))}" lon="${escapeXml(String(coords[0]))}">` +
              (coords.length > 2 && coords[2] !== null ? `<ele>${escapeXml(String(coords[2]))}</ele>` : '') +
              (timeStr ? `<time>${escapeXml(timeStr)}</time>` : '') +
              `</trkpt>\n`;
          });
          if (segmentPoints) {
            trackSegments += `<trkseg>\n${segmentPoints}</trkseg>\n`;
          }
          timeIndexOffset += lineCoords.length; // Increment offset for next line
        });
        if (trackSegments) {
          gpxTracks += `<trk>\n` +
            (title ? `<name>${escapeXml(title)}</name>\n` : '') +
            (description ? `<desc>${escapeXml(description)}</desc>\n` : '') +
            (link ? `<link href="${escapeXml(link)}"><text>Link</text></link>\n` : '') +
            trackSegments +
            `</trk>\n`;
        }
        break;
      }

      case "Polygon": {
        let trackSegments = '';
        let timeIndexOffset = 0;
        geometry.coordinates.forEach(ringCoords => {
          let segmentPoints = '';
          ringCoords.forEach((coords, index) => {
            const currentTimeIndex = timeIndexOffset + index;
            const timeStr = times && currentTimeIndex < times.length ? formatTime(times[currentTimeIndex]) : undefined; // Now calls outer formatTime
            segmentPoints += `<trkpt lat="${escapeXml(String(coords[1]))}" lon="${escapeXml(String(coords[0]))}">` +
              (coords.length > 2 && coords[2] !== null ? `<ele>${escapeXml(String(coords[2]))}</ele>` : '') +
              (timeStr ? `<time>${escapeXml(timeStr)}</time>` : '') +
              `</trkpt>\n`;
          });
          if (segmentPoints) {
            trackSegments += `<trkseg>\n${segmentPoints}</trkseg>\n`;
          }
          timeIndexOffset += ringCoords.length;
        });
        if (trackSegments) {
          gpxTracks += `<trk>\n` +
            (title ? `<name>${escapeXml(title)}</name>\n` : '') +
            (description ? `<desc>${escapeXml(description)}</desc>\n` : '') +
            (link ? `<link href="${escapeXml(link)}"><text>Link</text></link>\n` : '') +
            trackSegments +
            `</trk>\n`;
        }
        break;
      }
      case "MultiPolygon": {
        let timeIndexOffset = 0;
        geometry.coordinates.forEach(polygonCoords => {
          let trackSegments = '';
          polygonCoords.forEach(ringCoords => {
            let segmentPoints = '';
            ringCoords.forEach((coords, index) => {
              const currentTimeIndex = timeIndexOffset + index;
              const timeStr = times && currentTimeIndex < times.length ? formatTime(times[currentTimeIndex]) : undefined; // Now calls outer formatTime
              segmentPoints += `<trkpt lat="${escapeXml(String(coords[1]))}" lon="${escapeXml(String(coords[0]))}">` +
                (coords.length > 2 && coords[2] !== null ? `<ele>${escapeXml(String(coords[2]))}</ele>` : '') +
                (timeStr ? `<time>${escapeXml(timeStr)}</time>` : '') +
                `</trkpt>\n`;
              timeIndexOffset++;
            });
            if (segmentPoints) {
              trackSegments += `<trkseg>\n${segmentPoints}</trkseg>\n`;
            }
          });
          if (trackSegments) {
            gpxTracks += `<trk>\n` +
              (title ? `<name>${escapeXml(title)}</name>\n` : '') +
              (description ? `<desc>${escapeXml(description)}</desc>\n` : '') +
              (link ? `<link href="${escapeXml(link)}"><text>Link</text></link>\n` : '') +
              trackSegments +
              `</trk>\n`;
          }
        });
        break;
      }
      case "GeometryCollection": {
        geometry.geometries.forEach(geom => {
          const pseudoFeature: GeoJSON.Feature = {
            type: "Feature",
            properties: properties,
            geometry: geom
          };
          processFeature(pseudoFeature);
        });
        break;
      }

      default:
        console.warn(`Unsupported GeoJSON geometry type: ${(geometry as any)?.type}`);
        break;
    }
  }; // end of processFeature

  // --- Iterate through features and process them ---
  features.forEach(processFeature);

  // --- Assemble the final GPX string ---
  let gpx = `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<gpx version="1.1" creator="${escapeXml(mergedOptions.creator)}" ` +
    `xmlns="http://www.topografix.com/GPX/1/1" ` +
    `xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ` +
    `xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">\n`;

  // Add metadata if provided
  if (mergedOptions.metadata) {
    gpx += `<metadata>\n`;
    if (mergedOptions.metadata.name) gpx += `<name>${escapeXml(String(mergedOptions.metadata.name))}</name>\n`;
    if (mergedOptions.metadata.desc) gpx += `<desc>${escapeXml(String(mergedOptions.metadata.desc))}</desc>\n`;
    if (mergedOptions.metadata.author) gpx += `<author><name>${escapeXml(String(mergedOptions.metadata.author))}</name></author>\n`;
    if (mergedOptions.metadata.time) {
      // This call should now work correctly
      const metaTime = formatTime(mergedOptions.metadata.time);
      if (metaTime) gpx += `<time>${escapeXml(metaTime)}</time>\n`;
    }
    gpx += `</metadata>\n`;
  }

  // Add waypoints and tracks
  gpx += gpxWaypoints;
  gpx += gpxTracks;

  gpx += `</gpx>`;

  return gpx;
}
